// src/components/admin/tabs/AiTasksTab.jsx
// AI 任务总闸：17 张指标卡（任务 8 + Worker 4 + 旧任务池 5）、任务表、
// 全链路详情面板（DetailBlock / TraceList）、重试 / 取消 / 重排 / 标记错误状态，5s 轮询。
// 视觉：工作台语言（Panel / StatCard / StatusBadge / TypeBadge / font-mono-num），业务逻辑逐字保留。
// props 契约：({ projectId, projectOptions, onProjectSelect, selectedProjectId, onProjectsChanged })
// 本 tab 只用到 projectId / projectOptions，其余由父级传入时忽略（与原实现一致）。
import { appConfirm } from '../../../services/appDialogs';
import React, { useCallback, useState } from "react";
import { RefreshCw, RotateCcw, XCircle, Database } from "lucide-react";
import {
  fetchAiTaskOverview,
  fetchAiTasks,
  fetchAiTaskFullTrace,
  retryAiTask,
  cancelAiTask,
  requeueStaleAiTasks,
  updateAiTaskErrorStatus,
} from "../../../services/adminService";
import { useSequentialPolling } from "../../../hooks/useSequentialPolling";
import { displayUserId, shortText, formatCostSpec, formatTraceTime, useAdminScope } from "../adminUtils";
import {
  Panel,
  StatCard,
  EmptyText,
  ErrorNotice,
  FilterInput,
  FilterSelect,
  PrimaryButton,
  SecondaryButton,
  IconAction,
  StatusBadge,
  TypeBadge,
  DetailBlock,
  TraceList,
  AdminScopeFilterBar,
} from "../adminUi.jsx";

// 任务状态配色（tab 专属，adminUi 未提供映射）
const statusColor = (status) => {
  if (status === 'success') return 'var(--color-success)';
  if (status === 'failed') return 'var(--color-error)';
  if (status === 'processing') return 'var(--color-warning)';
  if (status === 'pending') return 'var(--color-info)';
  if (status === 'submission_unknown') return 'var(--color-error)';
  return 'var(--color-text-muted)';
};

const AiTasksTab = ({ projectId, projectOptions }) => {
  const [overview, setOverview] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ status: '', type: '', source: '', limit: 200 });
  const [scope, setScope] = useAdminScope(projectId);

  const load = useCallback(async ({ signal } = {}) => {
    setLoading(true);
    setError('');
    try {
      const effectiveFilters = { ...filters, projectId: scope.projectId, userId: scope.userId };
      const [overviewRes, tasksRes] = await Promise.all([
        fetchAiTaskOverview(effectiveFilters, { signal }),
        fetchAiTasks(effectiveFilters, { signal }),
      ]);
      if (signal?.aborted) return;
      setOverview(overviewRes);
      setTasks(tasksRes || []);
      setLoadedOnce(true);
    } catch (e) {
      if (signal?.aborted) return;
      setError(e?.message || '加载失败');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [filters, scope.projectId, scope.userId]);

  const refreshTasks = useSequentialPolling(load, 5000);

  const loadDetail = async (id) => {
    if (!id) return;
    try {
      setSelected(await fetchAiTaskFullTrace(id));
    } catch (e) {
      setError(e?.message || '读取详情失败');
    }
  };

  const runAction = async (action, id) => {
    if (!id) return;
    if (action === 'cancel' && !(await appConfirm('确定取消/停止这个任务？自动流会在下一个检查点安全退出。'))) return;
    setError('');
    try {
      if (action === 'retry') await retryAiTask(id);
      if (action === 'cancel') await cancelAiTask(id);
      await refreshTasks();
      const selectedTask = selected?.task || selected;
      if (selectedTask?.internal_task_id === id || selectedTask?.internalTaskId === id || selected?.internalTaskId === id) {
        await loadDetail(id);
      }
    } catch (e) {
      setError(e?.message || '操作失败');
    }
  };

  const overviewData = overview?.taskOverview || {};
  const worker = overview?.worker || {};
  const legacyPool = overview?.legacyPool || {};
  const selectedTask = selected?.task || selected;
  const traceCounts = selected?.counts || {};

  const taskStats = [
    ['总任务', overviewData.total],
    ['排队', overviewData.pending],
    ['执行中', overviewData.processing],
    ['提交待对账', overviewData.submissionUnknown, 'var(--color-error)'],
    ['成功', overviewData.success, 'var(--color-success)'],
    ['失败', overviewData.failed, 'var(--color-error)'],
    ['取消', overviewData.canceled],
    ['DB排队', overviewData.managedPending],
    ['DB执行中', overviewData.managedProcessing],
  ];
  const workerStats = [
    ['Worker 并发', `${worker.activeCount ?? 0}/${worker.configuredConcurrency ?? 0}`],
    ['线程池', `${worker.poolSize ?? 0}/${worker.maximumPoolSize ?? 0}`],
    ['Worker内部队列', worker.executorQueueSize ?? 0],
    ['已执行', worker.completedTaskCount ?? 0],
  ];
  const legacyStats = [
    ['旧任务池执行中', `${legacyPool.activeThreads ?? 0}/${legacyPool.poolSize ?? 0}`],
    ['旧任务池排队', `${legacyPool.queueSize ?? 0}/${legacyPool.queueCapacity ?? 0}`],
    ['旧任务池剩余', legacyPool.remainingQueueCapacity ?? 0],
    ['旧任务池活跃', legacyPool.liveTasks ?? 0],
    ['旧任务池拒绝', legacyPool.rejectedTasks ?? 0],
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          每 5 秒刷新。这里查看所有 AI 生成任务的内部 ID、状态、错误和后台 worker 队列。
        </div>
        <div className="flex gap-2 shrink-0">
          <SecondaryButton
            onClick={async () => { await requeueStaleAiTasks(360); await refreshTasks(); }}
            className="inline-flex items-center gap-1.5"
          >
            <RotateCcw size={14} /> 重排 6 小时卡住任务
          </SecondaryButton>
          <PrimaryButton onClick={refreshTasks} className="inline-flex items-center gap-1.5">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> 刷新
          </PrimaryButton>
        </div>
      </div>

      {error && <ErrorNotice message={error} />}

      {!loadedOnce && loading ? (
        <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3">
            {Array.from({ length: 9 }).map((_, i) => <div key={i} className="skeleton h-[62px]" />)}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-[62px]" />)}
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3">
            {taskStats.map(([label, value, color]) => (
              <StatCard key={label} label={label} value={value ?? 0} color={color} />
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {workerStats.map(([label, value]) => (
              <StatCard key={label} label={label} value={value} />
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {legacyStats.map(([label, value]) => (
              <StatCard key={label} label={label} value={value} />
            ))}
          </div>
        </>
      )}

      <AdminScopeFilterBar
        scope={scope}
        setScope={setScope}
        projectOptions={projectOptions}
        onRefresh={refreshTasks}
        loading={loading}
      >
        <FilterInput label="来源" value={filters.source} onChange={(v) => setFilters(f => ({ ...f, source: v }))} />
        <FilterSelect label="类型" value={filters.type} options={['', 'text', 'image', 'video', 'audio']} onChange={(v) => setFilters(f => ({ ...f, type: v }))} />
        <FilterSelect label="状态" value={filters.status} options={['', 'pending', 'processing', 'submission_unknown', 'success', 'failed', 'canceled']} onChange={(v) => setFilters(f => ({ ...f, status: v }))} />
        <FilterInput label="条数" type="number" value={filters.limit} onChange={(v) => setFilters(f => ({ ...f, limit: v }))} />
      </AdminScopeFilterBar>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-4">
        <Panel
          title="任务列表"
          sub="点击行查看全链路详情"
          right={<span className="font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{tasks.length} 条</span>}
        >
          {!loadedOnce && loading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4, 5].map(i => <div key={i} className="skeleton h-8" />)}
            </div>
          ) : (
            <div className="overflow-x-auto -mx-1 px-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-label" style={{ textAlign: "left" }}>
                    {['时间', '任务ID', '类型', '状态', '用户', '来源', '模型', '耗时', '操作'].map(h => (
                      <th key={h} className="font-medium pb-2.5 pr-4 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tasks.map(task => {
                    const id = task.internal_task_id || task.internalTaskId;
                    return (
                      <tr key={id} className="cursor-pointer hover:bg-[var(--color-bg-tertiary)] transition-colors duration-150" style={{ borderTop: '1px solid var(--color-border-secondary)' }} onClick={() => loadDetail(id)}>
                        <td className="py-2 pr-4 whitespace-nowrap font-mono-num text-2xs" style={{ color: 'var(--color-text-muted)' }}>{formatTraceTime(task.created_at || task.createdAt)}</td>
                        <td className="py-2 pr-4 font-mono-num text-xs" style={{ color: 'var(--color-info)' }}>{id}</td>
                        <td className="py-2 pr-4"><TypeBadge type={task.type} /></td>
                        <td className="py-2 pr-4"><StatusBadge label={task.status} color={statusColor(task.status)} /></td>
                        <td className="py-2 pr-4 text-xs">{shortText(displayUserId(task.user_id || task.userId), 16)}</td>
                        <td className="py-2 pr-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>{shortText(task.source, 28)}</td>
                        <td className="py-2 pr-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>{shortText(task.model || task.sub_model || task.subModel, 24)}</td>
                        <td className="py-2 pr-4 font-mono-num text-xs">{task.duration_ms || task.durationMs ? `${Math.round((task.duration_ms || task.durationMs) / 1000)}s` : '-'}</td>
                        <td className="py-2 pr-4">
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <IconAction title="重试" onClick={() => runAction('retry', id)} icon={RotateCcw} />
                            <IconAction title="取消 pending / 停止自动流" onClick={() => runAction('cancel', id)} icon={XCircle} danger />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!tasks.length && <EmptyText text="没有匹配任务" />}
            </div>
          )}
        </Panel>

        <Panel
          title={<span className="inline-flex items-center gap-2"><Database size={14} /> 任务详情</span>}
          className="min-h-[420px]"
        >
          {!selectedTask ? (
            <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>点击左侧任务查看 prompt、payload、结果和错误。</div>
          ) : (
            <div className="space-y-3 text-xs">
              <div className="font-mono-num break-all" style={{ color: 'var(--color-info)' }}>{selectedTask.internal_task_id || selectedTask.internalTaskId || selected.internalTaskId}</div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  ['生成日志', traceCounts.generationLogs ?? selected?.generationLogs?.length ?? 0],
                  ['费用记录', traceCounts.costRecords ?? selected?.costRecords?.length ?? 0],
                  ['资源入库', traceCounts.resources ?? selected?.resources?.length ?? 0],
                  ['活动日志', traceCounts.activityLogs ?? selected?.activityLogs?.length ?? 0],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-md p-2" style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-secondary)' }}>
                    <div className="text-label">{label}</div>
                    <div className="font-mono-num text-sm font-semibold mt-0.5" style={{ color: 'var(--color-text-primary)' }}>{value}</div>
                  </div>
                ))}
              </div>
              {selectedTask.result_url || selectedTask.resultUrl ? (
                <a href={selectedTask.result_url || selectedTask.resultUrl} target="_blank" rel="noreferrer" className="block underline" style={{ color: 'var(--color-success)' }}>打开结果资源</a>
              ) : null}
              {selectedTask.status === 'failed' && (
                <div className="flex flex-wrap gap-2">
                  {['investigating', 'resolved', 'ignored'].map(status => (
                    <button
                      key={status}
                      type="button"
                      onClick={async () => {
                        const id = selectedTask.internal_task_id || selectedTask.internalTaskId || selected.internalTaskId;
                        await updateAiTaskErrorStatus(id, status);
                        await loadDetail(id);
                        await refreshTasks();
                      }}
                      className="h-7 px-2 rounded-md text-2xs font-medium border cursor-pointer transition-colors duration-150"
                      style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}
                    >
                      标记 {status}
                    </button>
                  ))}
                </div>
              )}
              <DetailBlock title="错误" value={selectedTask.error_message || selectedTask.errorMessage} />
              <DetailBlock title="Prompt" value={selectedTask.prompt_snapshot || selectedTask.promptSnapshot} />
              <DetailBlock title="请求 Payload" value={selectedTask.request_payload || selectedTask.requestPayload} />
              <DetailBlock title="结果 Payload" value={selectedTask.result_payload || selectedTask.resultPayload} />
              <TraceList title="费用记录" rows={selected?.costRecords} render={(row) => `¥${row.cost ?? '-'} · ${row.type || '-'} · ${row.model || '-'} · ${formatCostSpec(row)} · ${formatTraceTime(row.created_at || row.createdAt)}`} />
              <TraceList title="资源入库" rows={selected?.resources} render={(row) => `${row.typeCode || '-'} · ${row.name || '-'} · ${row.url || '-'}`} />
              <TraceList title="活动日志" rows={selected?.activityLogs} render={(row) => `${row.status || '-'} · ${row.action || '-'} · ${formatTraceTime(row.created_at || row.createdAt)}`} />
              <TraceList title="生成日志" rows={selected?.generationLogs} render={(row) => `${row.status || '-'} · ${row.type || '-'} · ${row.provider || row.model || '-'} · ${formatTraceTime(row.created_at || row.createdAt)}`} />
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default AiTasksTab;
